import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, errorMessage } from "../../shared/api/errors.ts";
import type { components } from "../../shared/api/schema";
import { useApiClient } from "../../shared/session/SessionProvider.tsx";
import { Button } from "../../shared/ui/Button/Button.tsx";
import styles from "./EvaPage.module.css";

type Playback = components["schemas"]["KaraokePlayback"];
type Action = "start" | "pause" | "resume" | "stop";

const ACTIVE_POLL_MS = 3000;
const IDLE_POLL_MS = 10000;

export function formatSongTime(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** Позиция по последнему heartbeat; пока песня играет, досчитывается локально до следующего опроса. */
function useLivePosition(playback: Playback | null | undefined, reportedAt: number): number {
  const [now, setNow] = useState(() => Date.now());
  const playing = playback?.state === "playing";
  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [playing]);
  if (!playback) return 0;
  const elapsed = playing ? Math.max(0, now - reportedAt) : 0;
  return Math.min(playback.positionMs + elapsed, playback.durationMs);
}

function statusText(value: components["schemas"]["KaraokeStatusResponse"] | undefined): string {
  if (!value) return "Проверяю трек…";
  if (!value.supported) return "Нужно обновить прошивку открытки";
  if (!value.online) return "Открытка не в сети";
  switch (value.playback?.state) {
    case "loading": return `Сохраняю трек на открытку: ${value.playback.downloadPercent}%`;
    case "playing": return "Играет на открытке";
    case "paused": return "На паузе";
    case "finished": return "Песня доиграла до конца";
    case "error": return `Ошибка на открытке: ${value.playback.error || "неизвестная"}`;
  }
  if (!value.ready) {
    return value.errorCode === "karaoke_assets_missing"
      ? "Песня ещё не подключена: нужны запись и слова с таймкодами"
      : "Не удалось проверить файлы песни. Попробуйте позже";
  }
  return "Песня готова к запуску на открытке";
}

export function AngelRR({ deviceId }: { deviceId: string }) {
  const client = useApiClient();
  const cache = useQueryClient();
  const queryKey = ["angel-rr", deviceId];
  const status = useQuery({
    queryKey,
    refetchInterval: (query) => {
      const state = query.state.data?.playback?.state;
      return state === "playing" || state === "loading" ? ACTIVE_POLL_MS : IDLE_POLL_MS;
    },
    queryFn: async () => {
      const { data, error, response } = await client.GET("/v1/karaoke/devices/{deviceId}", {
        params: { path: { deviceId } },
      });
      if (!data) throw ApiError.from(response, error);
      return data;
    },
  });
  const command = useMutation({
    mutationFn: async (action: Action) => {
      const { data, error, response } = await client.POST("/v1/karaoke/devices/{deviceId}/control", {
        params: { path: { deviceId } }, body: { action },
      });
      if (!data) throw ApiError.from(response, error);
      return data;
    },
    // Открытка шлёт heartbeat сразу после смены состояния; новый статус появляется почти мгновенно.
    onSuccess: () => { void cache.invalidateQueries({ queryKey }); },
  });
  const value = status.data;
  const playback = value?.online ? value.playback : null;
  const position = useLivePosition(playback, status.dataUpdatedAt);
  const state = playback?.state;
  const usable = !!value?.online && !!value.supported && !command.isPending;
  const primary: { action: Action; label: string } =
    state === "playing" ? { action: "pause", label: "Пауза" }
    : state === "paused" ? { action: "resume", label: "Продолжить" }
    : { action: "start", label: "Старт" };
  const primaryDisabled = !usable || (primary.action === "start" && (!value?.ready || state === "loading"));
  const stoppable = state === "playing" || state === "paused" || state === "loading";
  const showProgress = state === "playing" || state === "paused";
  return <section className={styles.card}>
    <h2 className={styles.cardTitle}>Angel RR</h2>
    <p>Kai Angel — Andy Warhol</p>
    <p className={state === "error" ? styles.error : styles.hint} role="status">{statusText(value)}</p>
    {showProgress && playback && <div className={styles.songProgress}>
      <progress aria-label="Позиция песни" max={playback.durationMs} value={position} />
      <span className={styles.hint}>{formatSongTime(position)} / {formatSongTime(playback.durationMs)}</span>
    </div>}
    {state === "loading" && playback && <progress aria-label="Сохранение трека" max={100} value={playback.downloadPercent} />}
    <div className={styles.controlButtons}>
      <Button disabled={primaryDisabled} loading={command.isPending && command.variables === primary.action}
        onClick={() => command.mutate(primary.action)}>{primary.label}</Button>
      <Button variant="secondary" disabled={!usable || !stoppable} onClick={() => command.mutate("stop")}>Стоп</Button>
    </div>
    {(status.error || command.error) && <p className={styles.error} role="alert">{errorMessage(status.error || command.error)}</p>}
  </section>;
}
